import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listEvents } from '../services/eventsService';
import { getLayoutPublic } from '../services/layoutsService';
import { getVendor } from '../services/vendorsService';
import { getUserProfile } from '../services/profilesService';
import { useAuth } from '../context/AuthContext';
import EmailRecipientsModal from './EmailRecipientsModal';
import { collectUniqueEmails, toCommaDelimitedEmailText } from '../utils/emailRecipients';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import './ProfilePage.css';

// Helper to build a quick label for a booth cell
const boothLabel = (cell) => {
  if (!cell) return '';
  const parts = [];
  if (cell.level) parts.push(cell.level);
  if (cell.position) parts.push(cell.position);
  parts.push(cell.id);
  return parts.filter(Boolean).join(' / ');
};

// Format cents stored in data as USD dollars for display
const fmtCurrency = (n) => {
  const cents = Number(n || 0);
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(dollars);
};

const parsePositiveMinutes = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const BoothsAdmin = () => {
  const navigate = useNavigate();
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [eventChoices, setEventChoices] = useState([]); // {eventId,eventName,layoutId,layoutName}
  const [eventId, setEventId] = useState('');
  const [layoutId, setLayoutId] = useState('');
  const [eventName, setEventName] = useState('');
  const [layoutName, setLayoutName] = useState('');
  const [eventWaitMinutes, setEventWaitMinutes] = useState(null); // shopping cart wait window

  // Data for table
  const [cells, setCells] = useState([]); // layout cells (non-invisible)
  const [rows, setRows] = useState([]); // combined booths with reservation overlay
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customLevels, setCustomLevels] = useState({}); // level definitions with pricing

  // Filters
  const [statusFilter, setStatusFilter] = useState('All'); // All | Available | Pending | On-Hold | Reserved
  const [boothTypeFilter, setBoothTypeFilter] = useState('All'); // All | Inline | Corner
  const [vendorTypeFilter, setVendorTypeFilter] = useState('');
  const [vendorNameFilter, setVendorNameFilter] = useState('');
  const [contactNameFilter, setContactNameFilter] = useState('');
  const [boothNumberFilter, setBoothNumberFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('All');
  const [sortBy, setSortBy] = useState('booth');
  const [sortDir, setSortDir] = useState('asc');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalText, setEmailModalText] = useState('');

  const { currentUser } = useAuth();

  // Load events with published layout (exclude archived)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEventsLoading(true);
        setEventsError('');
        const all = await listEvents();
        const havingLayout = (all || []).filter(ev => ev?.layoutId && (ev?.status || '').toLowerCase() !== 'archived');
        // Apply role-based filtering similar to ApplicationsAdmin/TransactionsAdmin:
        // - Admin: see all havingLayout entries
        // - EventAdmin: see events where their UID is in `eventAdmins`
        // - Organizer: see events where their UID is in `organizers`
        // - Others: see none
        let filtered = havingLayout;
        try {
          if (currentUser && currentUser.uid) {
            try {
              const prof = await getUserProfile(currentUser.uid);
              const uidLower = String(currentUser.uid || '').toLowerCase();
              if (prof?.userType === 'EventAdmin') {
                filtered = (havingLayout || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType === 'Organizer') {
                filtered = (havingLayout || []).filter(ev => Array.isArray(ev.organizers) && ev.organizers.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType !== 'Admin') {
                filtered = [];
              }
            } catch (profileErr) {
              filtered = [];
            }
          }
        } catch (e) {
          filtered = [];
        }

        const entries = [];
        for (const ev of filtered) {
          try {
            const ly = await getLayoutPublic(ev.layoutId);
            if ((ly?.status || '').toLowerCase() !== 'published') continue;
            entries.push({
              eventId: ev.id,
              eventName: ev.name || ev.id,
              layoutId: ly.id,
              layoutName: ly.name || ly.title || ly.id,
              shoppingCartWait: parsePositiveMinutes(ev?.shoppingCartWait),
            });
          } catch {}
        }
        entries.sort((a, b) => String(a.eventName).localeCompare(String(b.eventName)));
        if (!cancelled) {
          setEventChoices(entries);
          if (entries.length > 0) {
            const first = entries[0];
            setEventId(first.eventId);
            setLayoutId(first.layoutId);
            setEventName(first.eventName);
            setLayoutName(first.layoutName);
            setEventWaitMinutes(first.shoppingCartWait);
          }
        }
      } catch (e) {
        if (!cancelled) setEventsError('Failed to load events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Caches to avoid refetching profiles/vendors repeatedly during live updates
  const profileCacheRef = React.useRef(new Map());
  const vendorCacheRef = React.useRef(new Map());

  // Helper to get booth price based on level and corner status
  const getBoothPrice = React.useCallback((level, isCorner) => {
    const levelData = customLevels[level];
    if (!levelData) return 0;
    return isCorner ? (levelData.cornerPrice || 0) : (levelData.inlinePrice || 0);
  }, [customLevels]);

  // Helper to compose rows from cells + reservations, enriching with cached profile/vendor data
  const composeRows = React.useCallback(async (visibleCells, resDocs) => {
    // Normalize reservations, keep expired entries (we will optionally hide them later)
    const toMillis = (ts) => {
      try {
        if (!ts) return null;
        if (typeof ts.toMillis === 'function') return ts.toMillis();
        if (ts.seconds) return ts.seconds * 1000;
        const d = new Date(ts);
        return isNaN(d.getTime()) ? null : d.getTime();
      } catch { return null; }
    };
    const now = Date.now();
    const byBooth = new Map();
    (resDocs || []).forEach(r => { if (r.boothId) byBooth.set(r.boothId, r); });

      const baseRows = [];
      const neededProfileIds = new Set();
      const vendorIds = new Set();
      visibleCells.forEach(c => {
        const res = byBooth.get(c.id);
        // After migration: reservations have vendorId directly, and createdByProfileId for who created it
        if (res?.createdByProfileId) neededProfileIds.add(res.createdByProfileId);
        if (res?.vendorId) vendorIds.add(res.vendorId);
        const expiresMsExplicit = toMillis(res?.expiresAt);
        let effectiveExpiresMs = null;
        // For Pending: use explicit expiresAt or fallback to createdAt + eventWaitMinutes
        if (res?.status === 'Pending') {
          if (typeof expiresMsExplicit === 'number') {
            effectiveExpiresMs = expiresMsExplicit;
          } else {
            const createdMs = toMillis(res?.createdAt);
            if (typeof createdMs === 'number' && Number.isFinite(eventWaitMinutes) && eventWaitMinutes > 0) {
              const waitMs = eventWaitMinutes * 60 * 1000;
              effectiveExpiresMs = createdMs + waitMs;
            }
          }
        } else if (res?.status === 'On-Hold') {
          // For On-Hold only expired if explicit expiresAt provided
          if (typeof expiresMsExplicit === 'number') effectiveExpiresMs = expiresMsExplicit;
        }
        // Determine if the reservation has passed its expiry time
        const expiredByTime = (res?.status === 'Pending' || res?.status === 'On-Hold') && typeof effectiveExpiresMs === 'number' && effectiveExpiresMs <= now;
        // If expired by time, it should no longer be considered Pending/On-Hold — treat as Available
        const statusRaw = res?.status || 'Available';
        const status = expiredByTime ? 'Available' : statusRaw;
        // We no longer hide expired Pending/On-Hold entries; they become Available instead
        const isExpired = false;
        const isCorner = (c.corner === 'Y' || c.corner === true);
        const price = getBoothPrice(c.level, isCorner);
        // Read expiresAt directly from the reservation doc. If the doc provides
        // `expiresAt` (Timestamp/Date-like) use it; otherwise prefer numeric
        // `expiresAtMs` (milliseconds) as-is so the UI shows the raw stored value.
        const normalizedExpiresAt = (res?.expiresAt != null) ? res.expiresAt : (typeof res?.expiresAtMs === 'number' ? res.expiresAtMs : null);
        baseRows.push({
          boothId: c.id,
          label: boothLabel(c),
          boothNumber: c.number || c.id,
          level: c.level || '',
          position: c.position || '',
          boothType: isCorner ? 'Corner' : 'Inline',
          status,
          profileId: res?.createdByProfileId || '', // who created the reservation
          vendorId: res?.vendorId || '', // the vendor who owns it
          reservedAt: res?.reservedAt || null,
          createdAt: res?.createdAt || null,
          expiresAt: normalizedExpiresAt || null,
          updatedAt: res?.updatedAt || null,
          holdName: res?.holdName || '',
          reason: res?.reason || '',
          paymentStatus: status === 'Reserved' ? 'Paid' : (status === 'Pending' ? 'Unpaid' : ''),
          vendorName: '',
          vendorEmail: '',
          vendorPhone: '',
          vendorType: '',
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          isExpired,
          effectiveExpiresMs,
          price, // Add booth price
        });
      });    // Load missing profiles into cache
    const profileCache = profileCacheRef.current;
    const vendorCache = vendorCacheRef.current;
    const missingProfiles = Array.from(neededProfileIds).filter(pid => !profileCache.has(pid));
    if (missingProfiles.length > 0) {
      await Promise.all(missingProfiles.map(async pid => {
        try { const prof = await getUserProfile(pid); if (prof) profileCache.set(pid, prof); } catch {}
      }));
    }

    // Fill contact info from profiles
    baseRows.forEach(r => {
      if (!r.profileId) return;
      const p = profileCache.get(r.profileId);
      if (p) {
        r.contactName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
        r.contactEmail = p.email || '';
        r.contactPhone = p.phone || p.phoneNumber || '';
      }
    });

    // Load missing vendors into cache
    const missingVendors = Array.from(vendorIds).filter(vid => !vendorCache.has(vid));
    if (missingVendors.length > 0) {
      await Promise.all(missingVendors.map(async vid => {
        try { const v = await getVendor(vid); if (v) vendorCache.set(vid, v); } catch {}
      }));
    }

    // Fill vendor info
    baseRows.forEach(r => {
      if (r.vendorId && vendorCache.has(r.vendorId)) {
        const v = vendorCache.get(r.vendorId);
        r.vendorName = v.name || '';
        r.vendorEmail = v.email || '';
        const vendorCountryCode = String(v.businessAreaCode || v.countryCode || '').trim();
        const vendorLocalPhone = String(v.phoneNumber || v.phone || '').trim();
        r.vendorPhone = [vendorCountryCode, vendorLocalPhone].filter(Boolean).join(' ').trim();
        r.vendorType = v.type || '';
      }
    });

    return baseRows;
  }, [eventWaitMinutes, getBoothPrice]);

  // Load layout cells and custom levels when event/layout changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!eventId || !layoutId) { 
        setCells([]); 
        setCustomLevels({});
        return; 
      }
      setLoading(true);
      setError('');
      try {
        // Reset caches per event/layout to avoid stale associations
        profileCacheRef.current = new Map();
        vendorCacheRef.current = new Map();

        // Load layout cells and pricing once
        const ly = await getLayoutPublic(layoutId);
        const allCells = Array.isArray(ly?.cellData) ? ly.cellData : [];
        const visibleCells = allCells.filter(c => c && c.id && c.level && c.level !== 'Invisible' && c.status !== 'Invisible');
        if (cancelled) return;
        setCells(visibleCells);
        
        // Load custom levels with pricing from layout
        setCustomLevels(ly?.customLevels || {});
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load layout');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, layoutId]);

  // Subscribe to reservations and compose rows when cells/customLevels are ready
  useEffect(() => {
    if (!eventId || !layoutId || cells.length === 0) return;
    
    let cancelled = false;
    const col = collection(db, 'boothReservations');
    const qRef = query(col, where('eventId', '==', eventId), where('layoutId', '==', layoutId));
    
    const unsubscribe = onSnapshot(qRef, async (snap) => {
      if (cancelled) return;
      const resDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      try {
        const composed = await composeRows(cells, resDocs);
        if (!cancelled) setRows(composed);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to update booths');
      }
    }, (err) => {
      if (!cancelled) setError(err?.message || 'Live updates failed');
    });
    
    return () => {
      cancelled = true;
      try { if (typeof unsubscribe === 'function') unsubscribe(); } catch {}
    };
  }, [eventId, layoutId, cells, composeRows]);

  const filtered = useMemo(() => {
    const sMatch = (r) => (statusFilter === 'All' || r.status === statusFilter);
    const btMatch = (r) => (boothTypeFilter === 'All' || r.boothType === boothTypeFilter);
    const vn = vendorNameFilter.trim().toLowerCase();
    const cn = contactNameFilter.trim().toLowerCase();
    const vt = (vendorTypeFilter || '').trim().toLowerCase();
    const bn = (boothNumberFilter || '').trim().toLowerCase();
    const lvl = (levelFilter || 'All').trim();
    return rows.filter(r => {
      // Hide expired entries entirely per updated requirement
      if (r.isExpired) return false;
      if (!sMatch(r)) return false;
      if (!btMatch(r)) return false;
      if (lvl && lvl !== 'All' && (r.level || '') !== lvl) return false;
      if (vn && !(r.vendorName || '').toLowerCase().includes(vn)) return false;
      if (cn) {
        const full = (r.contactName || '').toLowerCase();
        if (!full.includes(cn)) return false;
      }
      if (vt && (r.vendorType || '').toLowerCase() !== vt) return false;
      if (bn) {
        const bval = String(r.boothNumber || r.label || '').toLowerCase();
        if (!bval.includes(bn)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, boothTypeFilter, vendorNameFilter, contactNameFilter, vendorTypeFilter, boothNumberFilter, levelFilter]);

  // Totals for the currently displayed (filtered) rows
  const summaryFiltered = useMemo(() => {
    const counts = { Available: 0, 'On-Hold': 0, Pending: 0, Reserved: 0 };
    filtered.forEach(r => {
      const s = r.status || 'Available';
      if (counts[s] == null) counts[s] = 0;
      counts[s] += 1;
    });
    return counts;
  }, [filtered]);

  const handleOpenFilteredEmailsModal = React.useCallback(() => {
    const emails = collectUniqueEmails(filtered, [
      (row) => row.vendorEmail,
      (row) => row.contactEmail,
    ]);
    setEmailModalText(toCommaDelimitedEmailText(emails));
    setShowEmailModal(true);
  }, [filtered]);

  // Sorted view of filtered rows
  const sortedFiltered = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const copy = Array.isArray(filtered) ? filtered.slice() : [];
    const toTs = (v) => {
      try {
        if (!v) return 0;
        if (typeof v.toMillis === 'function') return v.toMillis();
        if (v.seconds) return v.seconds * 1000;
        const d = new Date(v);
        return isNaN(d.getTime()) ? 0 : d.getTime();
      } catch { return 0; }
    };
    copy.sort((a, b) => {
      try {
        if (sortBy === 'booth') {
          const aa = String(a.boothNumber || a.label || '').toLowerCase();
          const bb = String(b.boothNumber || b.label || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'level') {
          const aa = String(a.level || '').toLowerCase();
          const bb = String(b.level || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'type') {
          const aa = String(a.boothType || '').toLowerCase();
          const bb = String(b.boothType || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'status') {
          const aa = String(a.status || '').toLowerCase();
          const bb = String(b.status || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'payment') {
          const aa = String(a.paymentStatus || '').toLowerCase();
          const bb = String(b.paymentStatus || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'vendor') {
          const aa = String(a.vendorName || '').toLowerCase();
          const bb = String(b.vendorName || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'vendorType') {
          const aa = String(a.vendorType || '').toLowerCase();
          const bb = String(b.vendorType || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'contact') {
          const aa = String(a.contactName || '').toLowerCase();
          const bb = String(b.contactName || '').toLowerCase();
          return aa === bb ? 0 : (aa > bb ? dir : -dir);
        }
        if (sortBy === 'requested') {
          return (toTs(a.createdAt) - toTs(b.createdAt)) * dir;
        }
        if (sortBy === 'updated') {
          return (toTs(a.updatedAt) - toTs(b.updatedAt)) * dir;
        }
        if (sortBy === 'expires') {
          return (toTs(a.expiresAt) - toTs(b.expiresAt)) * dir;
        }
        if (sortBy === 'reserved') {
          return (toTs(a.reservedAt) - toTs(b.reservedAt)) * dir;
        }
      } catch (e) {
        return 0;
      }
      return 0;
    });
    return copy;
  }, [filtered, sortBy, sortDir]);

  // Summary by Level table data
  const levelSummary = useMemo(() => {
    // Build base map from layout cells so Total reflects the layout definition
    const map = new Map(); // level -> {Available, 'On-Hold', Pending, Reserved, Total, ReservedRevenue, TotalRevenue}
    (cells || []).forEach(c => {
      if (!c) return;
      const lvl = (c.level || 'Unspecified');
      if (!map.has(lvl)) map.set(lvl, { Available: 0, 'On-Hold': 0, Pending: 0, Reserved: 0, Total: 0, ReservedRevenue: 0, TotalRevenue: 0 });
      const entry = map.get(lvl);
      entry.Total += 1;
    });

    // Overlay reservation/status counts from rows (respecting isExpired)
    (rows || []).forEach(r => {
      if (r.isExpired) return; // Exclude expired entries from status counts and revenues
      const lvl = r.level || 'Unspecified';
      if (!map.has(lvl)) map.set(lvl, { Available: 0, 'On-Hold': 0, Pending: 0, Reserved: 0, Total: 0, ReservedRevenue: 0, TotalRevenue: 0 });
      const entry = map.get(lvl);
      const s = r.status || 'Available';
      if (s === 'Reserved' || s === 'On-Hold' || s === 'Pending') {
        entry[s] = (entry[s] || 0) + 1;
      }
      // Revenue calculations follow previous behavior (only non-expired rows contribute)
      entry.TotalRevenue += (r.price || 0);
      if (s === 'Reserved') entry.ReservedRevenue += (r.price || 0);
    });

    // Compute Available as remainder
    for (const [lvl, entry] of map.entries()) {
      const nonAvailable = (entry['On-Hold'] || 0) + (entry.Pending || 0) + (entry.Reserved || 0);
      entry.Available = Math.max(0, (entry.Total || 0) - nonAvailable);
    }

    // Sort by natural level name
    return Array.from(map.entries()).sort((a,b) => String(a[0]).localeCompare(String(b[0])));
  }, [rows, cells]);

  // Overall totals across all rows (ignores filters)
  const summaryAll = useMemo(() => {
    const counts = {
      Available: 0,
      'On-Hold': 0,
      Pending: 0,
      Reserved: 0,
      Total: 0,
      ReservedRevenue: 0,
      TotalRevenue: 0
    };

    // Total booths come from layout cells
    counts.Total = (cells || []).filter(c => c && c.level && c.level !== 'Invisible' && c.status !== 'Invisible').length;

    // Aggregate status counts and revenue from rows (non-expired)
    rows.forEach(r => {
      if (r.isExpired) return;
      const s = r.status || 'Available';
      if (counts[s] == null) counts[s] = 0;
      counts[s] += 1;
      counts.TotalRevenue += (r.price || 0);
      if (s === 'Reserved') counts.ReservedRevenue += (r.price || 0);
    });

    // Available = Total - (On-Hold + Pending + Reserved)
    const nonAvail = (counts['On-Hold'] || 0) + (counts.Pending || 0) + (counts.Reserved || 0);
    counts.Available = Math.max(0, counts.Total - nonAvail);

    return counts;
  }, [rows, cells]);

  return (
    <div className="profile-container profile-wide-container">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
      </div>
      <h2 className="profile-title">Admin: Booths Overview</h2>

      {/* Event selection */}
      <div className="profile-grid" style={{ marginBottom: 12 }}>
        <div>
          <label className="profile-label">Event</label>
          <select
            className="profile-input"
            value={eventId || ''}
            onChange={(e) => {
              const val = e.target.value;
              const opt = eventChoices.find(p => p.eventId === val);
              setEventId(val);
              if (opt) {
                setEventName(opt.eventName);
                setLayoutId(opt.layoutId);
                setLayoutName(opt.layoutName);
                setEventWaitMinutes(opt.shoppingCartWait);
              } else { setEventName(val); }
            }}
            disabled={eventsLoading || eventChoices.length === 0}
          >
            {eventsLoading && <option value="">Loading events…</option>}
            {!eventsLoading && eventChoices.length === 0 && <option value="">No events</option>}
            {!eventsLoading && eventChoices.length > 0 && (
              <>
                <option value="" disabled>Select an event…</option>
                {eventChoices.map(p => (
                  <option key={p.eventId} value={p.eventId}>{p.eventName}</option>
                ))}
              </>
            )}
          </select>
          {eventId && <div className="profile-hint">Event ID: {eventId}</div>}
        </div>
        <div>
          <label className="profile-label">Layout</label>
          <input className="profile-input" value={layoutName || (layoutId || '')} readOnly />
          {layoutId && <div className="profile-hint">Layout ID: {layoutId}</div>}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="profile-hint">Selecting a different event will reload booth statuses for its published layout.</div>
        </div>
      </div>

      {/* Removed top status summary cards per request */}

      {/* Summary by Level */}
      <h3 className="profile-title" style={{ marginTop: 0 }}>Summary by Level</h3>
      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
        <table className="profile-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px' }}>Level</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Available</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>On-Hold</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Pending</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Reserved</th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Total</th>
              <th style={{ textAlign: 'right', padding: '8px' }}>Reserved Revenue</th>
              <th style={{ textAlign: 'right', padding: '8px' }}>Total Revenue</th>
            </tr>
          </thead>
          <tbody>
            {levelSummary.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 12, textAlign: 'center', color: '#666' }}>No data</td></tr>
            ) : (
              levelSummary.map(([lvl, c]) => (
                <tr key={lvl}>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{lvl}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{c['Available'] || 0}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{c['On-Hold'] || 0}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{c['Pending'] || 0}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{c['Reserved'] || 0}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{c['Total'] || 0}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right' }}>
                    {fmtCurrency(c['ReservedRevenue'] || 0)}
                  </td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right' }}>
                    {fmtCurrency(c['TotalRevenue'] || 0)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa' }}>All Levels Total</td>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa' }}>{summaryAll['Available'] || 0}</td>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa' }}>{summaryAll['On-Hold'] || 0}</td>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa' }}>{summaryAll['Pending'] || 0}</td>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa' }}>{summaryAll['Reserved'] || 0}</td>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa' }}>{summaryAll['Total'] || 0}</td>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa', textAlign: 'right' }}>
                {fmtCurrency(summaryAll['ReservedRevenue'] || 0)}
              </td>
              <td style={{ padding: '8px', borderTop: '2px solid #ccc', fontWeight: 700, background: '#fafafa', textAlign: 'right' }}>
                {fmtCurrency(summaryAll['TotalRevenue'] || 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ margin: 0 }}>Filters</h4>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="apply-btn"
              onClick={handleOpenFilteredEmailsModal}
              disabled={loading || filtered.length === 0}
              title={filtered.length === 0 ? 'No filtered rows to copy' : 'Copy vendor and primary contact emails for filtered rows'}
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              Copy Filtered Email IDs
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => {
                setStatusFilter('All');
                setBoothTypeFilter('All');
                setVendorTypeFilter('');
                setVendorNameFilter('');
                setContactNameFilter('');
                setBoothNumberFilter('');
                setLevelFilter('All');
              }}
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              Reset Filters
            </button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: 8 }}>
          <div>
            <label className="profile-label">Status</label>
            <select className="profile-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              {['All','Available','On-Hold','Pending','Reserved'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="profile-label">Level</label>
            <select className="profile-input" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
              <option value="All">All</option>
              {Array.from(new Set((cells || []).map(c => c.level).filter(Boolean))).sort().map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="profile-label">Booth Type</label>
            <select className="profile-input" value={boothTypeFilter} onChange={e => setBoothTypeFilter(e.target.value)}>
              {['All','Inline','Corner'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="profile-label">Booth #</label>
            <input className="profile-input" value={boothNumberFilter} onChange={e => setBoothNumberFilter(e.target.value)} placeholder="e.g. A12" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          <div>
            <label className="profile-label">Vendor Type</label>
            <input className="profile-input" value={vendorTypeFilter} onChange={e => setVendorTypeFilter(e.target.value)} placeholder="e.g. Food" />
          </div>
          <div>
            <label className="profile-label">Vendor Name</label>
            <input className="profile-input" value={vendorNameFilter} onChange={e => setVendorNameFilter(e.target.value)} placeholder="e.g. Acme" />
          </div>
          <div>
            <label className="profile-label">Contact Name</label>
            <input className="profile-input" value={contactNameFilter} onChange={e => setContactNameFilter(e.target.value)} placeholder="e.g. Jane Doe" />
          </div>
        </div>
      </div>

      {error && <div className="profile-error" style={{ marginBottom: 8 }}>{error}</div>}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        {!loading && (
          <div style={{ padding: 12, fontWeight: 700, borderBottom: '2px solid #ccc', background: '#fafafa', marginBottom: 8 }}>
            Totals — Available: {summaryFiltered['Available'] || 0} • On-Hold: {summaryFiltered['On-Hold'] || 0} • Pending: {summaryFiltered['Pending'] || 0} • Reserved: {summaryFiltered['Reserved'] || 0}
          </div>
        )}
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className="profile-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('booth'); setSortDir(sortBy === 'booth' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Booth {sortBy === 'booth' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('level'); setSortDir(sortBy === 'level' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Level {sortBy === 'level' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('type'); setSortDir(sortBy === 'type' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Type {sortBy === 'type' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('status'); setSortDir(sortBy === 'status' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Status {sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('payment'); setSortDir(sortBy === 'payment' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Payment {sortBy === 'payment' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('vendor'); setSortDir(sortBy === 'vendor' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Vendor {sortBy === 'vendor' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('vendorType'); setSortDir(sortBy === 'vendorType' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Vendor Type {sortBy === 'vendorType' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('contact'); setSortDir(sortBy === 'contact' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Contact {sortBy === 'contact' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('requested'); setSortDir(sortBy === 'requested' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Requested {sortBy === 'requested' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('updated'); setSortDir(sortBy === 'updated' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Updated {sortBy === 'updated' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('expires'); setSortDir(sortBy === 'expires' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Expires {sortBy === 'expires' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>
                    <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('reserved'); setSortDir(sortBy === 'reserved' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                      Reserved {sortBy === 'reserved' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                </tr>
            </thead>
            <tbody>
              {sortedFiltered.length === 0 ? (
                <tr><td colSpan={12} style={{ padding: 12, textAlign: 'center', color: '#666' }}>No booths match the filters</td></tr>
              ) : (
                sortedFiltered.map(r => (
                  <tr key={r.boothId}>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.boothNumber || r.label}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.level || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.boothType || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                      {r.status}{r.isExpired ? ' (Expired)' : ''}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.paymentStatus || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.vendorName || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.vendorType || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.contactName || ''}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                      {(() => {
                        try {
                          const ts = r.createdAt;
                          if (!ts) return '';
                          if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toLocaleString();
                          if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
                          const d = new Date(ts);
                          return isNaN(d.getTime()) ? '' : d.toLocaleString();
                        } catch { return ''; }
                      })()}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                      {(() => {
                        try {
                          const ts = r.updatedAt;
                          if (!ts) return '';
                          if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toLocaleString();
                          if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
                          const d = new Date(ts);
                          return isNaN(d.getTime()) ? '' : d.toLocaleString();
                        } catch { return ''; }
                      })()}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                      {(() => {
                        try {
                          const ts = r.expiresAt;
                          if (!ts) return '';
                          if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toLocaleString();
                          if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
                          const d = new Date(ts);
                          return isNaN(d.getTime()) ? '' : d.toLocaleString();
                        } catch { return ''; }
                      })()}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                      {(() => {
                        try {
                          const ts = r.reservedAt;
                          if (!ts) return '';
                          if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toLocaleString();
                          if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
                          const d = new Date(ts);
                          return isNaN(d.getTime()) ? '' : d.toLocaleString();
                        } catch { return ''; }
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            
          </table>
        )}
      </div>
      <EmailRecipientsModal
        open={showEmailModal}
        title="Filtered Email IDs"
        subtitle="Vendor and primary contact emails for current filtered booths. Edit as needed, then copy."
        initialValue={emailModalText}
        onClose={() => setShowEmailModal(false)}
      />
    </div>
  );
};

export default BoothsAdmin;
